import React from 'react';
import Icon from '../AppIcon';

const WorkflowProgressTracker = ({ 
  steps = [], 
  currentStep = 0, 
  onStepClick,
  className = '' 
}) => {
  const defaultSteps = [
    { id: 1, label: 'Initialize', description: 'Setup workflow parameters' },
    { id: 2, label: 'Validate', description: 'Verify data integrity' },
    { id: 3, label: 'Process', description: 'Execute main workflow' },
    { id: 4, label: 'Review', description: 'Quality assurance check' },
    { id: 5, label: 'Complete', description: 'Finalize and submit' }
  ];

  const workflowSteps = steps?.length > 0 ? steps : defaultSteps;

  const getStepStatus = (stepIndex) => {
    if (stepIndex < currentStep) return 'completed';
    if (stepIndex === currentStep) return 'current';
    return 'pending';
  };

  const getStepIcon = (stepIndex, status) => {
    switch (status) {
      case 'completed':
        return 'CheckCircle';
      case 'current':
        return 'Circle';
      default:
        return 'Circle';
    }
  };

  const handleStepClick = (stepIndex) => {
    if (onStepClick && stepIndex <= currentStep) {
      onStepClick(stepIndex);
    }
  };

  return (
    <div className={`bg-card border border-border rounded-lg p-4 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-card-foreground">Workflow Progress</h3>
        <span className="text-xs text-muted-foreground">
          Step {currentStep + 1} of {workflowSteps?.length}
        </span>
      </div>
      {/* Desktop Progress Bar */}
      <div className="hidden md:flex items-center space-x-4">
        {workflowSteps?.map((step, index) => {
          const status = getStepStatus(index);
          const isClickable = onStepClick && index <= currentStep;

          return (
            <React.Fragment key={step?.id}>
              <button
                onClick={() => handleStepClick(index)}
                disabled={!isClickable}
                className={`flex flex-col items-center space-y-2 p-2 rounded-lg transition-smooth ${
                  isClickable ? 'hover:bg-muted cursor-pointer' : 'cursor-default'
                } ${status === 'current' ? 'bg-primary/10' : ''}`}
              >
                <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 transition-smooth ${
                  status === 'completed' 
                    ? 'bg-success border-success text-success-foreground' 
                    : status === 'current' ?'bg-primary border-primary text-primary-foreground' :'bg-background border-muted-foreground text-muted-foreground'
                }`}>
                  {status === 'completed' ? (
                    <Icon name="Check" size={16} />
                  ) : (
                    <span className="text-xs font-medium">{index + 1}</span>
                  )}
                </div>
                <div className="text-center">
                  <div className={`text-xs font-medium ${
                    status === 'current' ? 'text-primary' : 'text-card-foreground'
                  }`}>
                    {step?.label}
                  </div>
                  <div className="text-xs text-muted-foreground max-w-20 truncate">
                    {step?.description}
                  </div>
                </div>
              </button>
              {/* Connector Line */}
              {index < workflowSteps?.length - 1 && (
                <div className={`flex-1 h-0.5 transition-smooth ${
                  index < currentStep ? 'bg-success' : 'bg-border'
                }`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
      {/* Mobile Progress Bar */}
      <div className="md:hidden">
        <div className="flex items-center space-x-2 mb-3">
          <div className="flex-1 bg-border rounded-full h-2">
            <div 
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentStep + 1) / workflowSteps?.length) * 100}%` }}
            />
          </div>
        </div>

        <div className="flex justify-between text-xs">
          <span className="text-card-foreground font-medium">
            {workflowSteps?.[currentStep]?.label}
          </span>
          <span className="text-muted-foreground">
            {currentStep + 1}/{workflowSteps?.length}
          </span>
        </div>

        <div className="text-xs text-muted-foreground mt-1">
          {workflowSteps?.[currentStep]?.description}
        </div>
      </div>
    </div>
  );
};

export default WorkflowProgressTracker;